import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, Phone, Plus, Search } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import AssistantEditor from "@/features/assistants/components/AssistantEditor";
import CreateAssistantDialog, {
  type NewAssistantContent,
} from "@/features/assistants/components/CreateAssistantDialog";
import { assistantsApi, type AttachedNumber } from "@/features/assistants/api";
import { useAssistants } from "@/features/assistants/hooks";

/** The assistant you last had open, so coming back to this page resumes your
 * work instead of dropping you on whichever one happens to sort first.
 *
 * Kept in localStorage rather than on the server: it's a per-browser habit,
 * not something about the assistant. A stale id is harmless — the page falls
 * back to the first assistant when it no longer matches anything, which also
 * covers a deleted assistant and a switched workspace.
 */
const LAST_OPENED_KEY = "assistants:last-opened";

function readLastOpened(): string | null {
  try {
    return localStorage.getItem(LAST_OPENED_KEY);
  } catch {
    return null; // private mode / storage disabled
  }
}

function writeLastOpened(id: string) {
  try {
    localStorage.setItem(LAST_OPENED_KEY, id);
  } catch {
    /* not worth failing the page over */
  }
}

/** Which numbers answer with this assistant — scannable at a glance, so you
 * can tell the live ones from the ones only reachable through Talk.
 *
 * Read-only here by design: the row is a single click target that opens the
 * assistant, and the number's own "Answered by" picker owns the mapping. The
 * full list with providers is in the title, since one line fits one number. */
function NumberLine({ numbers }: { numbers: AttachedNumber[] }) {
  if (numbers.length === 0) return null;
  const [first, ...rest] = numbers;
  const full = numbers
    .map((n) => `${n.number} (${n.channel === "whatsapp" ? "WhatsApp" : n.provider})`)
    .join(", ");
  return (
    <div
      title={full}
      className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground"
    >
      <Phone className="size-3 shrink-0" />
      <span className="truncate font-mono">{first.number}</span>
      {rest.length > 0 && <span className="shrink-0">+{rest.length}</span>}
    </div>
  );
}

/**
 * Assistants workspace.
 *
 * Two panes: the editor (main) plus an always-visible list on the right.
 * Assistants are persisted per tenant via /api/v1/assistants/.
 */
export default function AssistantsPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const assistantsQ = useAssistants();
  const assistants = assistantsQ.data ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(
    id ?? readLastOpened(),
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [query, setQuery] = useState("");

  const selected = assistants.find((a) => a.id === selectedId) ?? assistants[0];

  // Remember what you were working on. Written from `selected`, not from
  // `selectedId`, so a remembered assistant that has since been deleted (or
  // belongs to a workspace you've switched away from) is replaced by whatever
  // was actually shown instead of being remembered forever.
  useEffect(() => {
    if (selected) writeLastOpened(selected.id);
  }, [selected]);

  const createMut = useMutation({
    mutationFn: assistantsApi.create,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["assistants"] });
      setSelectedId(created.id);
      setCreateOpen(false);
    },
    onError: () => toast.error("Couldn't create the assistant. Please try again."),
  });

  function handleCreate(content: NewAssistantContent) {
    createMut.mutate({
      name: content.name,
      first_message: content.firstMessage,
      system_prompt: content.systemPrompt,
      first_message_mode: content.firstMessage ? "assistant_first" : "user_first",
      source_template: content.templateId,
    });
  }

  const filtered = assistants.filter((a) =>
    a.name.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div className="flex flex-col gap-5 lg:flex-row-reverse lg:items-start">
      {/* Right-hand assistants list (always visible) */}
      <aside className="w-full shrink-0 lg:w-72">
        <div className="rounded-xl border bg-card">
          <div className="flex items-center justify-between border-b px-3 py-2.5">
            <span className="text-sm font-semibold">Assistants</span>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              Create
            </Button>
          </div>
          <div className="border-b p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search assistants"
                className="h-8 pl-8 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1 p-2">
            {assistantsQ.isLoading ? (
              <>
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
                <Skeleton className="h-14 w-full" />
              </>
            ) : (
              <>
                {filtered.map((a) => {
                  const active = selected && a.id === selected.id;
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => setSelectedId(a.id)}
                      className={cn(
                        "flex items-start gap-2.5 rounded-lg border p-2.5 text-left transition-colors",
                        active
                          ? "border-primary bg-primary/5"
                          : "border-transparent hover:bg-muted",
                      )}
                    >
                      <div className="flex size-8 shrink-0 items-center justify-center rounded-md border bg-background">
                        <Bot className="size-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{a.name}</span>
                          <span
                            className={cn(
                              "shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium",
                              a.is_published
                                ? "bg-emerald-500/15 text-emerald-500"
                                : "bg-muted text-muted-foreground",
                            )}
                          >
                            {a.is_published ? "Published" : "Draft"}
                          </span>
                        </div>
                        <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
                          {a.transcriber?.provider_label ?? "—"} · {a.llm?.name ?? "—"} ·{" "}
                          {a.voice?.name ?? "—"}
                        </div>
                        <NumberLine numbers={a.attached_numbers ?? []} />
                      </div>
                    </button>
                  );
                })}
                {filtered.length === 0 && assistants.length > 0 && (
                  <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                    No assistants match "{query}".
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Editor (main) */}
      <div className="min-w-0 flex-1">
        {assistantsQ.isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : selected ? (
          // Keyed so switching assistants remounts the editor's local state.
          <AssistantEditor
            key={selected.id}
            assistant={selected}
            onDuplicated={(newId) => setSelectedId(newId)}
            onDeleted={() => setSelectedId(null)}
          />
        ) : (
          <div className="flex min-h-[360px] flex-col items-center justify-center gap-3 rounded-xl border bg-card p-8 text-center">
            <div className="flex size-12 items-center justify-center rounded-full border bg-background">
              <Bot className="size-6 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold">No assistants yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Create your first assistant from a quickstart template or a blank slate.
              </p>
            </div>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              Create Assistant
            </Button>
          </div>
        )}
      </div>

      <CreateAssistantDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreate={handleCreate}
        creating={createMut.isPending}
      />
    </div>
  );
}
